import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/jwt.service";
import store from "@/core/services/store";
import router from "@/router.js";
import { LOGOUT } from "@/core/services/store/auth.module";

/**
 * Service to call HTTP request via Axios
 */

const base_url = process.env.VUE_APP_API_ENDPOINT;

const handleRequestError = (error) => {
  const response = error && error.response;

  if (response && response.status == 401) {
    store.dispatch(LOGOUT, { revoke: false }).then(() => {
      if (router.currentRoute.name !== "login") {
        return router.push({ name: "login" }).catch(() => {});
      }
    });
  }

  return Promise.reject(error);
};



window.base_url = base_url;
window.configuration = JSON.parse(window.localStorage.getItem("configuration"));

const ApiService = {
  bridge: null,

  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = base_url;
    Vue.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const response = error && error.response;
        if (response && response.status === 428) {
          const target =
            response.data && response.data.code === "mfa_setup_required"
              ? "/profile/security"
              : "/profile/change-password";
          if (router.currentRoute.path !== target)
            router.push(target).catch(() => {});
        }
        return Promise.reject(error);
      }
    );

    this.bridge = axios.create({
      // baseURL: "http://localhost:8002/api/",
      baseURL: process.env.VUE_APP_API_SATUSEHAT_ENDPOINT,
      headers: {
        Authorization: `Bearer ${JwtService.getTokenBridge()}`,
      },
    });
    this.setHeader();
  },

  /**
   * Set the default HTTP request headers
   */
  setHeader() {
    Vue.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${JwtService.getToken()}`;

    let config = null;
    try {
      config = JSON.parse(localStorage.getItem("tenantconfig"));
    } catch (e) {}

    if (config && (config.database_ip || config.database_name)) {
      Vue.axios.defaults.headers.common["hostip"] = config.database_ip;
      Vue.axios.defaults.headers.common["name"] = config.database_name;
    }
    this.setBranchHeader(window.localStorage.getItem("medhop_branch_id"));
  },

  setBranchHeader(branchId) {
    if (branchId) Vue.axios.defaults.headers.common["X-Branch-Id"] = branchId;
    else delete Vue.axios.defaults.headers.common["X-Branch-Id"];
  },

  setHeaderBridge(bearerToken = null) {
    let accessToken = bearerToken;
    if (accessToken == null) {
      accessToken = JwtService.getTokenBridge();
    }
    this.bridge.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${accessToken}`;
    this.bridge.defaults.headers.Authorization = `Bearer ${accessToken}`;

    this.bridge.defaults.headers.common["accesstoken"] =
      JwtService.getTokenSatuSehat();
  },

  setDbConfigHeader(dbConfig) {
    Vue.axios.defaults.headers.common["hostip"] = dbConfig.database_ip;

    Vue.axios.defaults.headers.common["name"] = dbConfig.database_name;
  },

  query(resource, params) {
    return Vue.axios.get(resource, params).catch(handleRequestError);
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  get(resource, slug = "") {
    return Vue.axios.get(`${resource}${slug}`).catch(handleRequestError);
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params) {
    if (
      params &&
      Object.values(params).some(
        (value) => typeof File !== "undefined" && value instanceof File
      )
    ) {
      const formData = new FormData();
      Object.keys(params).forEach((key) => {
        if (
          params[key] !== null &&
          params[key] !== undefined &&
          params[key] !== ""
        )
          formData.append(key, params[key]);
      });
      return Vue.axios.post(`${resource}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return Vue.axios.post(`${resource}`, params);
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params);
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  // put(resource, params) {
  //   return Vue.axios.put(`${resource}`, params)
  // },
  put(resource, params) {
    resource += "?_method=PUT";
    if (
      params &&
      Object.values(params).some(
        (value) => typeof File !== "undefined" && value instanceof File
      )
    ) {
      const formData = new FormData();
      Object.keys(params).forEach((key) => {
        if (
          params[key] !== null &&
          params[key] !== undefined &&
          params[key] !== ""
        )
          formData.append(key, params[key]);
      });
      return Vue.axios.post(`${resource}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return Vue.axios.post(`${resource}`, params);
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    if (resource.includes("?")) {
      resource += "&_method=DELETE";
    } else {
      resource += "?_method=DELETE";
    }
    return Vue.axios.post(resource).catch(handleRequestError);
  },
};

export default ApiService;
